import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { MarkType, NodeType } from '@atlaskit/editor-prosemirror/model';
import type { Selection } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import type { EditorPluginAIProvider } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../../ui/prebuilt/config-items/config-items';
import type {
	InvokedFrom,
	InvokedFromTriggerMethod,
} from '../../ui/utils/analytics/analyticsFlowTypes';
import type { Positions } from '../../ui/utils/xstate/get-ai-experience-service';

export interface EndExperienceAction {
	data: { ignoredStartTriggerSelection?: Selection; modalDecorationSet?: DecorationSet };
	type: 'END_EXPERIENCE';
}
export interface ShowModalAction {
	data: {
		configItem: EditorPluginAIConfigItem;
		has3pSources?: boolean;
		hideLoadingScreen?: boolean;
		initialPositions: Positions;
		initialPrompt?: string;
		initialSelection?: Selection;
		interactionSessionId?: string;
		isCreate?: boolean;
		lastTriggeredFrom?: InvokedFrom;
		modalDecorationSet: DecorationSet;
		modalMountedTimeStamp: number;
		nudgeMetrics?: Record<string, number | undefined>;
		promptVersion?: string;
		singleInstrumentationId?: string;
		sourceProduct?: string | null;
		traceId?: string;
		triggeredFor?: NodeType;
		triggeredForMark?: MarkType;
		triggerMethod?: InvokedFromTriggerMethod;
		userADFInput?: JSONDocNode;
		willAISuggestEmoji?: boolean;
		withAcceptReject?: boolean;
		withStreamingHighlight?: boolean;
	};
	type: 'SHOW_MODAL';
}

export interface UpdateModalDecorationSetAction {
	data: {
		modalDecorationSet: DecorationSet;
	};
	type: 'UPDATE_MODAL_DECORATION_SET';
}

export interface AgentInfo {
	agentId?: string;
	/** Atlassian identity account ID — used to generate the agent avatar via GeneratedAvatar */
	agentIdentityAccountId?: string | null;
	agentName?: string;
	/** External config reference (named_id) for OOTB agent avatar lookup. */
	agentNamedId?: string | null;
}

export interface SetExperienceSessionAction {
	data: {
		agentInfo?: AgentInfo;
		promptVersion?: string;
		sessionId: string;
		traceId?: string;
	};
	type: 'START_SESSION' | 'END_SESSION';
}
export interface UpdateTargetIdAction {
	data: {
		targetId: string | number;
	};
	type: 'UPDATE_TARGET_ID';
}

export interface EndInlineRovoStreamingAction {
	type: 'END_INLINE_ROVO_STREAMING';
}

/** Stash a candidate targetId without exposing it to consumers yet. */
export interface StageInlineRovoStreamingAction {
	data: { targetId: string };
	type: 'STAGE_INLINE_ROVO_STREAMING';
}

/** Promote the staged targetId to the live `inlineRovoStreamingTargetId`. */
export interface CommitInlineRovoStreamingAction {
	type: 'COMMIT_INLINE_ROVO_STREAMING';
}

/** Show the floating "ready" bar after an inline edit completes (visual only). */
export interface ShowInlineEditReadyAction {
	type: 'SHOW_INLINE_EDIT_READY';
}

/** Dismiss the floating inline-edit "ready" bar. */
export interface DismissInlineEditReadyAction {
	type: 'DISMISS_INLINE_EDIT_READY';
}

/**
 * Calculates positions from modalDecorationSet and initialPositions.
 * This logic mirrors the calculation done in ContentComponent.
 */
function calculatePositions(
	modalDecorationSet: DecorationSet,
	initialPositions?: Positions,
): Positions | undefined {
	// Prioritise getting position from inlineDecoration,
	// otherwise get decoration position from start or end widget
	let positions: [number, number] = [0, 0];
	const decoration = modalDecorationSet
		.find(
			undefined,
			undefined,
			(spec) =>
				spec.key === 'modalMountPointDecoration' || spec.key === 'modalMountPointRangeDecoration',
		)
		.shift();

	if (decoration) {
		// when there is decoration, it means it's text selection
		// we need to use the initial positions
		// as the decoration positions are not accurate when there the selection is empty
		if (!initialPositions) {
			return undefined;
		}
		positions = initialPositions;
	} else {
		const nodeDecorations = modalDecorationSet.find(
			undefined,
			undefined,
			(spec) => spec.key === 'ai-nodeDecoration',
		);
		if (nodeDecorations.length > 0) {
			const startPos = Math.min(...nodeDecorations.map((decoration) => decoration.from));
			const endPos = Math.max(...nodeDecorations.map((decoration) => decoration.to));
			positions = [startPos, endPos];
		} else {
			return undefined;
		}
	}

	return positions;
}

export interface AIDecorationExperiencePluginState {
	/**
	 * Information about the agent currently editing the page, used to render an
	 * agent-specific telepointer during in-document streaming.
	 */
	agentInfo?: AgentInfo;
	configItem?: EditorPluginAIConfigItem;
	/**
	 * This is the current session id which the active AI experiecne is using.
	 */
	currentSessionId?: string;

	dispatchAnalyticsEvent: PMPluginFactoryParams['dispatchAnalyticsEvent'];
	/**
	 * Whether or not the AI experience is from a 3p product when user invokes AI action from smartlink toolbar.
	 */
	has3pSources?: boolean;
	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	/**
	 * This stores a selection which will be used to determine if the space shortcut which starts an experience should be ignored.
	 */
	ignoredSpaceTriggerSelection?: Selection;
	initialPositions?: Positions;
	initialPrompt?: string;
	/**
	 * this is used to restore the selection when the in-document streaming is discarded
	 */
	initialSelection?: Selection;
	/**
	 * Id of the node being processed by the inline Rovo prompt. Set on
	 * prompt submit, cleared on complete/abort. Cleared explicitly via
	 * `endInlineRovoStreaming` — NOT on unmount (fires repeatedly).
	 */
	inlineRovoStreamingTargetId?: string | null;
	/**
	 * This is a unique id representing the current interaction. An interaction is from the moment the experience is invoked
	 * through to when the experience is ended. This id will remain set even past the end of an experience and will only update
	 * once when the experience is started.
	 */
	interactionSessionId?: string;
	/**
	 * Whether the payload is for create action
	 */
	isCreate?: boolean;
	isInDocumentStreamingEnabled?: boolean;
	/**
	 * When true, the floating inline-edit "ready" bar is shown after completion.
	 * Visual only — set on complete, cleared on View/Close. Survives the prompt
	 * teardown because it lives in plugin state, not the prompt component.
	 */
	isInlineEditReady?: boolean;
	isRunning: boolean;
	/**
	 * Explicitly tracks when the AcceptRejectScreenWithLogic is being shown to the user
	 */
	isShowingAcceptRejectScreen?: boolean;
	lastTriggeredFrom?: InvokedFrom;
	modalDecorationSet: DecorationSet;
	modalMountedTimeStamp?: number;
	/**
	 * This is the session id which will be used when the next AI experience is started.
	 */
	nextSessionId?: string;
	/**
	 * Nudge metrics will be used passed in analytics events.
	 * It includes detailed metrics for improve writing nudge.
	 */
	nudgeMetrics?: Record<string, number | undefined>;
	/**
	 * Captured at toolbar-click time before the AI modal clears the selection.
	 * Promoted to `inlineRovoStreamingTargetId` on prompt submit.
	 */
	pendingInlineRovoStreamingTargetId?: string | null;
	/**
	 * Calculated positions for the modal based on decorations and initial positions.
	 * This is computed by the reducer when decorations change.
	 */
	positions?: Positions;
	product?: EditorPluginAIProvider['product'];
	/**
	 * The version of the prompt used for the AI experience.
	 */
	promptVersion?: string;
	/**
	 * If the session has ended
	 */
	sessionEnded?: boolean;

	/**
	 * If the session has started
	 */
	sessionStarted?: boolean;

	/**
	 * The id of the experience to trace to "viewed" and "initiated".
	 * Required for unified AI instrumentation:
	 * https://hello.atlassian.net/wiki/spaces/AAI/pages/3770721410/Unified+Event+Instrumentation+For+AI+Features
	 */
	singleInstrumentationId?: string;
	/**
	 * The source 3p product when user invokes AI action from smartlink toolbar.
	 */
	sourceProduct?: string | null;
	/**
	 * The id of the AIFC content target, which will be supplied to backend APIs,
	 * and yielded in every chunk of network stream events. This is used to determine
	 * which content target to use for content mutations.
	 */
	targetId?: string | number;

	/**
	 * The id of the traceId received from backend to trace the AI experience through the backend to frontend.
	 */
	traceId?: string;

	triggeredFor?: NodeType;

	triggeredForMark?: MarkType;

	triggerMethod?: InvokedFromTriggerMethod;

	/**
	 * ADF content to pre-fill the inline prompt with (e.g. a skill chip).
	 */
	userADFInput?: JSONDocNode;
	willAISuggestEmoji?: boolean;
	withAcceptReject?: boolean;

	withStreamingHighlight?: boolean;
}

export function reducer(
	state: AIDecorationExperiencePluginState,
	action:
		| EndExperienceAction
		| ShowModalAction
		| UpdateModalDecorationSetAction
		| SetExperienceSessionAction
		| UpdateTargetIdAction
		| EndInlineRovoStreamingAction
		| StageInlineRovoStreamingAction
		| CommitInlineRovoStreamingAction
		| ShowInlineEditReadyAction
		| DismissInlineEditReadyAction,
): AIDecorationExperiencePluginState {
	switch (action.type) {
		case 'END_EXPERIENCE':
			// When the experience ends the plugin state needs to reset everything back to the initial plugin state
			return {
				modalDecorationSet: DecorationSet.empty,
				dispatchAnalyticsEvent: state.dispatchAnalyticsEvent,
				initialPositions: undefined,
				initialSelection: undefined,
				positions: undefined,
				...action.data,
				isRunning: false,
				isShowingAcceptRejectScreen: false,
				targetId: state.targetId,
				currentSessionId:
					state.currentSessionId === state.nextSessionId ? state.currentSessionId : undefined,
				nextSessionId: state.nextSessionId,
				hideLoadingScreen: state.nextSessionId ? state.hideLoadingScreen : false,
				sessionEnded: false,
				isInDocumentStreamingEnabled: state.isInDocumentStreamingEnabled,
				isCreate: undefined,
				singleInstrumentationId: undefined,
				// Clear agent info so it doesn't leak into the next session if END_EXPERIENCE fires
				// before END_SESSION (which the SHOW_MODAL comment below acknowledges can happen).
				agentInfo: undefined,
				// Preserve the inline-edit ready flag so the floating "ready" bar survives
				// the experience teardown that fires on completion (cleared on View/Close).
				isInlineEditReady: state.isInlineEditReady,
				product: state.product,
			};
		case 'SHOW_MODAL':
			return {
				...state,
				...action.data,
				isRunning: true,
				isShowingAcceptRejectScreen: action.data.withAcceptReject && !state.nextSessionId,
				currentSessionId: state.nextSessionId,
				// if somehow END_EXPERIENCE is called before END_SESSION, reset sessionEnded to false here
				sessionEnded: false,
				positions: calculatePositions(action.data.modalDecorationSet, action.data.initialPositions),
				ignoredSpaceTriggerSelection: undefined,
				isCreate: action.data.isCreate,
				// A new experience starting dismisses any lingering inline-edit ready bar.
				isInlineEditReady: false,
			};
		case 'UPDATE_MODAL_DECORATION_SET':
			return {
				...state,
				modalDecorationSet: action.data.modalDecorationSet,
				positions: calculatePositions(action.data.modalDecorationSet, state.initialPositions),
			};
		case 'START_SESSION':
			return {
				...state,
				nextSessionId: action.data.sessionId,
				traceId: action.data.traceId,
				promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
					? action.data.promptVersion
					: undefined,
				agentInfo: action.data.agentInfo,
				sessionStarted: true,
				sessionEnded: false,
				isShowingAcceptRejectScreen: false,
			};
		case 'END_SESSION':
			return {
				...state,
				nextSessionId: action.data.sessionId,
				currentSessionId: state.isRunning ? state.currentSessionId : action.data.sessionId,
				hideLoadingScreen: false,
				sessionStarted: false,
				sessionEnded: true,
				traceId: undefined,
				promptVersion: undefined,
				agentInfo: undefined,
				singleInstrumentationId: undefined,
			};
		case 'UPDATE_TARGET_ID':
			return {
				...state,
				targetId: action.data.targetId,
			};
		case 'END_INLINE_ROVO_STREAMING':
			return {
				...state,
				inlineRovoStreamingTargetId: null,
				pendingInlineRovoStreamingTargetId: null,
			};
		case 'SHOW_INLINE_EDIT_READY':
			return { ...state, isInlineEditReady: true };
		case 'DISMISS_INLINE_EDIT_READY':
			return { ...state, isInlineEditReady: false };
		case 'STAGE_INLINE_ROVO_STREAMING':
			return { ...state, pendingInlineRovoStreamingTargetId: action.data.targetId };
		case 'COMMIT_INLINE_ROVO_STREAMING': {
			const promoted = state.pendingInlineRovoStreamingTargetId ?? null;
			if (promoted == null) {
				// Nothing to commit (e.g. modal opened via a non-toolbar path).
				return state;
			}
			return {
				...state,
				inlineRovoStreamingTargetId: promoted,
				pendingInlineRovoStreamingTargetId: null,
			};
		}
		default:
			return state;
	}
}
